import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, ChevronRight } from "lucide-react";
import api, { gbp } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const SORTS = [["featured", "Featured"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["rating", "Top Rated"], ["newest", "Newest"]];

function Filters({ brands, params, setParam, price, setPrice, applyPrice }) {
  const selectedBrands = (params.get("brand") || "").split(",").filter(Boolean);
  const toggleBrand = (b) => {
    const next = selectedBrands.includes(b) ? selectedBrands.filter((x) => x !== b) : [...selectedBrands, b];
    setParam("brand", next.join(","));
  };
  return (
    <div className="space-y-8">
      <div>
        <h4 className="overline mb-4">Price Range</h4>
        <Slider min={0} max={400} step={5} value={price} onValueChange={setPrice} onValueCommit={applyPrice} data-testid="price-slider" className="mb-3" />
        <div className="flex justify-between text-sm text-muted-foreground"><span>{gbp(price[0])}</span><span>{gbp(price[1])}</span></div>
      </div>
      <div>
        <h4 className="overline mb-4">Availability</h4>
        <label className="flex items-center justify-between text-sm">In stock only
          <Switch checked={params.get("in_stock") === "true"} onCheckedChange={(v) => setParam("in_stock", v ? "true" : "")} data-testid="filter-instock" />
        </label>
        <label className="mt-3 flex items-center justify-between text-sm">On offer
          <Switch checked={params.get("on_offer") === "true"} onCheckedChange={(v) => setParam("on_offer", v ? "true" : "")} data-testid="filter-offer" />
        </label>
        <label className="mt-3 flex items-center justify-between text-sm">New arrivals
          <Switch checked={params.get("is_new") === "true"} onCheckedChange={(v) => setParam("is_new", v ? "true" : "")} data-testid="filter-new" />
        </label>
      </div>
      <div>
        <h4 className="overline mb-4">Minimum Rating</h4>
        <div className="flex gap-2">
          {[0, 4, 4.5].map((r) => (
            <button key={r} onClick={() => setParam("min_rating", r ? String(r) : "")} className={`border px-3 py-1.5 text-xs font-medium transition-colors ${(+(params.get("min_rating") || 0)) === r ? "border-accent text-accent" : "border-border text-muted-foreground hover:border-accent"}`}>{r === 0 ? "All" : `${r}★+`}</button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="overline mb-4">Brand</h4>
        <div className="max-h-64 space-y-2.5 overflow-y-auto no-scrollbar pr-1">
          {brands.map((b) => (
            <label key={b.name} className="flex cursor-pointer items-center gap-2.5 text-sm">
              <Checkbox checked={selectedBrands.includes(b.name)} onCheckedChange={() => toggleBrand(b.name)} data-testid={`filter-brand-${b.name}`} />
              <span className="flex-1">{b.name}</span>
              <span className="text-xs text-muted-foreground">{b.count}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ products: [], total: 0 });
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState([0, 400]);

  useEffect(() => { api.get("/brands").then((r) => setBrands(r.data)).catch(() => {}); }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const qs = Object.fromEntries(params.entries());
    try { const { data } = await api.get("/products", { params: { ...qs, limit: 48 } }); setData(data); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
  };
  const applyPrice = (v) => { const next = new URLSearchParams(params); next.set("min_price", v[0]); next.set("max_price", v[1]); setParams(next, { replace: true }); };

  const discipline = params.get("discipline");
  const category = params.get("category");
  const subcategory = params.get("subcategory");
  const q = params.get("q");
  const brand = params.get("brand");
  const title = subcategory || category || discipline || (q ? `Search: "${q}"` : brand || "All Products");

  const clearAll = () => setParams(discipline ? new URLSearchParams({ discipline }) : new URLSearchParams());

  return (
    <div className="container-furn py-8">
      <nav className="mb-6 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-accent">Home</Link><ChevronRight className="h-3 w-3" />
        <Link to="/shop" className="hover:text-accent">Shop</Link>
        {discipline && <><ChevronRight className="h-3 w-3" /><span className="text-foreground">{discipline}</span></>}
        {category && <><ChevronRight className="h-3 w-3" /><span className="text-foreground">{category}</span></>}
      </nav>

      <div className="mb-8 flex flex-col gap-2 border-b border-border pb-6">
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight md:text-5xl">{title}</h1>
        <p className="text-sm text-muted-foreground">{data.total} products</p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <Filters brands={brands} params={params} setParam={setParam} price={price} setPrice={setPrice} applyPrice={applyPrice} />
        </aside>

        <div>
          <div className="mb-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sheet>
                <SheetTrigger asChild>
                  <button data-testid="mobile-filter-btn" className="flex items-center gap-2 border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-widest lg:hidden"><SlidersHorizontal className="h-4 w-4" /> Filters</button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85vw] overflow-y-auto bg-background">
                  <h3 className="mb-6 font-display text-lg font-semibold uppercase">Filters</h3>
                  <Filters brands={brands} params={params} setParam={setParam} price={price} setPrice={setPrice} applyPrice={applyPrice} />
                </SheetContent>
              </Sheet>
              <button onClick={clearAll} className="hidden items-center gap-1 text-xs text-muted-foreground hover:text-accent sm:flex"><X className="h-3.5 w-3.5" /> Clear filters</button>
            </div>
            <Select value={params.get("sort") || "featured"} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="w-[190px]" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse bg-secondary" />)}</div>
          ) : data.products.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-lg text-muted-foreground">No products match your filters.</p>
              <button onClick={clearAll} className="mt-4 bg-primary px-6 py-3 text-xs font-bold uppercase tracking-widest text-primary-foreground">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="product-grid">
              {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
